import Bluebird from 'bluebird';
import Raven from 'raven-js';
import CacheG from '../../../services/cache';
import queryfy from '../../../services/queryfy';
import { strToJSON } from '../../../services/strings';
import { generateID } from '../../../services/utils';

declare const ActiveXObject: (version: string) => void;

function getXHR(): XMLHttpRequest {
    if (typeof XMLHttpRequest !== 'undefined') {
        return new XMLHttpRequest();
    }

    const versions = [
        'MSXML2.XmlHttp.6.0',
        'MSXML2.XmlHttp.5.0',
        'MSXML2.XmlHttp.4.0',
        'MSXML2.XmlHttp.3.0',
        'MSXML2.XmlHttp.2.0',
        'Microsoft.XmlHttp'
    ];

    let xhr: XMLHttpRequest | undefined;
    for (const version of versions) {
        try {
            xhr = new ActiveXObject(version);
            break;
        } catch (e) {
            // Ignore
        }
    }

    if (!xhr) {
        throw new Error('Could not find a supported XHR method!');
    }

    return xhr;
}

class XHRError extends Error {
    public xhr: XMLHttpRequest;

    constructor(xhr: XMLHttpRequest, msg?: string) {
        msg = msg || 'XHR responded with code ' + xhr.status;
        super(msg);
        this.xhr = xhr;
        this.name = 'XHRError';
    }
}

// tslint:disable-next-line:max-classes-per-file
export class AjaxError extends Error {
    public response: object | string;
    public code: number;

    constructor(response: object | string, code: number, message?: string) {
        super(message);
        this.response = response;
        this.code = code;
        this.name = 'AjaxError';
    }
}

function send(method: string, url: string, data: object | null,
    headers?: object | null, async = true): Bluebird<XMLHttpRequest> {

    const xhr = getXHR();
    xhr.open(method, url, async);

    if (headers) {
        for (const key in headers) {
            if (!headers.hasOwnProperty(key)) continue;
            xhr.setRequestHeader(key, headers[key]);
        }
    }

    return new Bluebird<XMLHttpRequest>((resolve, reject) => {
        xhr.onreadystatechange = () => {
            if (xhr.readyState !== XMLHttpRequest.DONE) return;

            if (xhr.status >= 200 && xhr.status < 300) resolve(xhr);
            else {
                const msg = xhr.status === 0
                    ? 'XHR blocked by client'
                    : '';

                reject(new XHRError(xhr, msg));
            }
        };

        xhr.send(JSON.stringify(data));
    });
}

const cache = new CacheG<any>();

export default function ajax<T>(method: string, url: string, data?: object | null,
    options: {
        headers?: object, logURL?: string, groupURL?: string,
        ignoreClientErrors?: boolean
    } = {}): Bluebird<T> {

    const { headers = null, ignoreClientErrors } = options;
    let
        { logURL = url } = options,
        { groupURL = logURL } = options;

    method = method.toUpperCase();

    if (method === 'GET') {
        if (data) {
            // Convert data object to query string
            const query = '?' + queryfy(data);
            url += query;
            logURL += query;
            groupURL += query;

            data = null;
        }

        // Check if already in cache
        const fromCache = cache.find(url);
        if (fromCache) return Bluebird.resolve(fromCache);

        // Check if same call is in progress
        const inProgress = getInProgressPromise<T>('GET', url);
        if (inProgress !== null) return inProgress;
    }

    // Keep track of calls in progress
    const id = generateID();

    // Execute XHR call
    const startTime: number = new Date().getTime();

    const promise = send(method, url, data || null, headers).then(xhr => {
        // Call succeeded
        const responseText = xhr.responseText.trim(),
            responseJSON: { data?: any } | null = strToJSON(responseText),
            response = responseJSON || responseText,
            duration = new Date().getTime() - startTime;

        // Log response information
        if (logURL) {
            console.log(`${method} ${logURL} (${duration}ms)`);
        }

        Raven.captureBreadcrumb({
            category: 'xhr',
            data: { method, url, payload: data, headers, response, duration },
            level: 'info'
        });

        // Add response to cache
        if (method === 'GET') {
            cache.set(url, response);
        }

        return response as T;
    }, (e: XHRError) => {
        // Call failed
        const xhr = e.xhr,
            responseText = xhr.responseText.trim(),
            responseJSON: { data?: any } | null = strToJSON(responseText),
            response = responseJSON || responseText,
            duration = new Date().getTime() - startTime;

        // Log response information
        const status = xhr.status;
        if (logURL) {
            const logStatus = status === 0
                ? 'BLOCKED'
                : status;

            console.log(`[${logStatus}] ${method} ${logURL} (${duration}ms)`);
        }

        if (responseJSON) {
            const errorMsg = (responseJSON as any).message || '';
            console.log('Error response:', errorMsg || '', responseJSON);
        }

        if (status !== 404 && status >= 400 && status < 500 && !ignoreClientErrors
            || status >= 500 && status < 600) {
            Raven.captureBreadcrumb({
                message: `Error ${status}: ${method} ${groupURL}`,
                category: 'xhr',
                data: { method, url, payload: data, headers, response, duration },
                level: 'error'
            });
        }

        return Bluebird.reject(new AjaxError(response, status, e.message));
    }).finally(() => delete callsDict[id]);

    callsDict[id] = { method, path: url, promise };

    return promise;
}

const callsDict: {
    [key: string]: {
        method: string,
        path: string,
        promise: Bluebird<any>
    }
} = {};

function getInProgressPromise<T>(method: string, path: string): Bluebird<T> | null {
    const promises: Array<Bluebird<T>> = Object.keys(callsDict)
        .map(key => callsDict[key])
        .filter(x => x.method === method && x.path === path)
        .map(x => x.promise);

    return promises.length > 0 ? promises[0] : null;
}

export function getCallsInProgress(): number {
    return Object.keys(callsDict).length;
}

export function ajaxDone(): Bluebird<any> {
    if (getCallsInProgress() === 0) return Bluebird.resolve();
    return Bluebird.all(Object.keys(callsDict)
        .map(key => callsDict[key])
        .map(x => x.promise))
        .then(() => ajaxDone());
}



// WEBPACK FOOTER //
// ./node_modules/tslint-loader!./src/assets/scripts/services/ajax.ts